import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import PaginationComponent from "modules/blog/components/Pagination";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PostListItem from "modules/common/components/PostListItem";
import PressLogos from "modules/common/components/PressLogos";
import PropTypes from "prop-types";
import { useTopMenu } from "utils/menuUtils";
import StickyBottomBarAdapter from "../adapters/StickyBottomBar";
import { BlogSubscribeBox } from "../components";

const { Box, Grid } = Chakra;

const BlogHomeTemplate = ({
  story,
  stories,
  cmsLanguagesData,
  currentPage,
  total,
  serverSideStoryblokPages,
  whiteListDomains,
}) => {
  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const firstRowStoriesList = stories.slice(0, 3);
  const secondRowStoriesList = stories.slice(3, 5);
  const thirdRowStoriesList = stories.slice(5, 11);
  const lastRowStoriesList = stories.slice(11, 15);

  const {
    content: {
      metaDescription,
      metaTitle,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={`${metaTitle} - AirHelp`} description={metaDescription} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />

      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <Box width="100%" maxWidth="1202px" px={4} mx="auto" as="main">
            <Grid mt={32} templateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }} columnGap={8}>
              <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
              {firstRowStoriesList.map((blogpost, index) => (
                <PostListItem
                  key={blogpost.uuid}
                  isHero={index === 0}
                  showExcerpt={index !== 0}
                  showReadMore={index !== 0}
                  story={blogpost}
                />
              ))}
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} columnGap={8}>
              {secondRowStoriesList.map((blogpost) => (
                <PostListItem key={blogpost.uuid} story={blogpost} />
              ))}
              <Box mb={24}>
                <BlogSubscribeBox mobileView />
              </Box>
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} columnGap={8}>
              {thirdRowStoriesList.map((blogpost) => (
                <PostListItem key={blogpost.uuid} story={blogpost} />
              ))}
            </Grid>
          </Box>
          <Box bgColor="greyscale.300" pt={24}>
            <Box width="100%" maxWidth="1202px" px={4} mx="auto">
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr 1fr" }} columnGap={8}>
                {lastRowStoriesList.map((blogpost) => (
                  <PostListItem key={blogpost.uuid} showImage={false} showExcerpt={false} story={blogpost} />
                ))}
              </Grid>
            </Box>
          </Box>
          <Box width="100%" maxWidth="1202px" px={4} mb={{ base: 16, md: 32 }} pt={24} mx="auto">
            <PaginationComponent page={currentPage} total={total} />
          </Box>
          <PressLogos />

          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const BlogHomeTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <BlogHomeTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default BlogHomeTemplateAdapter;

BlogHomeTemplate.propTypes = {
  story: PropTypes.shape({
    content: PropTypes.shape({
      metaTitle: PropTypes.string.isRequired,
      metaDescription: PropTypes.string.isRequired,
      breadcrumbsParentPage: PropTypes.shape({}),
      breadcrumbsPositionName: PropTypes.string,
    }),
  }).isRequired,
  stories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.shape({
        author: PropTypes.string,
        body: PropTypes.arrayOf(
          PropTypes.shape({
            component: PropTypes.string,
            content: PropTypes.shape({
              content: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
            }),
          })
        ),
      }),
    })
  ).isRequired,
  cmsLanguagesData: PropTypes.arrayOf(
    PropTypes.shape({
      full_slug: PropTypes.string,
    })
  ),
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  currentPage: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

BlogHomeTemplate.defaultProps = {
  cmsLanguagesData: [],
  whiteListDomains: [],
};
